import { useGetMainList } from "ra-core";
import { FC, createContext, useContext, useMemo, useRef } from "react";
import { RecPatch } from "@/dataProvider/tools";
import { DataOwnerGroupResponseDto, SecondaryClassificationResponseDto } from "@/gen/api/governance";
import { DocumentTemplateResponseDto, FileTagResponseDto, InfoTypeResponseDto, PrimaryClassification } from "@/gen/api/securefile";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { SimpleOption, SimpleOption2 } from "../governance/search/AmbientFacilitiesContext";


export interface FileCataAmbientDataContextProps {
  tags: (FileTagResponseDto & RecPatch & SimpleOption)[];
  classifications: (SecondaryClassificationResponseDto & RecPatch & SimpleOption)[];
  documentTypes: SimpleOption[];
  infoTypes: SimpleOption[];
  users: SimpleOption[];
  machines: SimpleOption2[];
  ownerGroups: SimpleOption[];
}

export const FileCataAmbientDataContext = createContext<FileCataAmbientDataContextProps>({} as any)

export const FileCataAmbientDataContextProvider : FC<{ 
  auditUser?:boolean, 
  accessUser?:boolean, 
  machine?:boolean,
  ownerGroup?:boolean,
  includePrimaryClassification?:boolean,
  startTime?: Date|null,
  endTime?: Date|null,
}> = ({ 
  auditUser, accessUser, machine, ownerGroup, includePrimaryClassification, startTime, endTime, children 
}) => {
  const { data: data1, ids: ids1 } = useGetMainList<FileTagResponseDto&RecPatch>('secure-files-tags', pages(), sorts(), {})
  const { data: data2, ids: ids2 } = useGetMainList<SecondaryClassificationResponseDto&RecPatch>('settings/governance/data-classification', pages(), sorts(), {})
  const { data: data3, ids: ids3 } = useGetMainList<InfoTypeResponseDto&RecPatch>('settings/securefile/info-types', pages(), sorts(), {})
  const { data: data4, ids: ids4 } = useGetMainList<DocumentTemplateResponseDto&RecPatch>('settings/securefile/document-types', pages(), sorts(), {})
    
  const optionsRef = useRef({
    auditUserOptions: {} as Record<number, string>,
    accessUserOptions: {} as Record<number, string>,
    machineOptions: new Set<string>(),
  })
  const { data: data5 } = useGetMainList(!auditUser || (!startTime && !endTime) ? 'noop' : 'secure-files/audit/user-options', pages(), sorts(), {startTime, endTime})
  const { data: data6 } = useGetMainList(!machine ? 'noop' : 'secure-files/machine-options', pages(), sorts(), {})
  const { data: data7 } = useGetMainList(!accessUser || (!startTime && !endTime) ? 'noop' : 'secure-files/access/user-options', pages(), sorts(), {startTime, endTime})
  const { data: data8, ids: ids8 } = useGetMainList<DataOwnerGroupResponseDto&RecPatch>(!ownerGroup ? 'noop' : 'settings/governance/data-owner-group', pages(), sorts(), {})

  
  const val = useMemo(() => {
    optionsRef.current.auditUserOptions = {...optionsRef.current.auditUserOptions, ...(data5 && data5[-1]?.data)}
    optionsRef.current.accessUserOptions = {...optionsRef.current.accessUserOptions, ...(data7 && data7[-1]?.data)}
    const users = Object.entries(
        auditUser ? optionsRef.current.auditUserOptions
      : accessUser ? optionsRef.current.accessUserOptions
      : {}
    ).map(kv => ({ key: +kv[0], text: kv[1]}) as SimpleOption).sort((a, b) => a.text < b.text ? -1 : 1);
    ((data6 && data6[-1]?.data as string[]) ?? []).forEach(m => optionsRef.current.machineOptions.add(m))
    const machines = [...optionsRef.current.machineOptions].sort((a, b) => a < b ? -1 : 1).map(m => ({ key: m, text: m}) as SimpleOption2)
    
    return ({
      tags: (ids1 ?? []).map((id) => {
        const t = data1![id as number]
        const s : SimpleOption = t as any
        s.key = t.tagId!
        s.text = t.name!
        return s as any
      }),
      classifications: (ids2 ?? [])
        .map((id) => {
          const c = data2![id as number]
          const s : SimpleOption = c as any
          s.key = c.secondaryClassificationId!
          s.text = `${c.primaryClassificationText} - ${c.name}`
          return s as any
        })
        .concat(!includePrimaryClassification ? [] : 
          [
            {key: -PrimaryClassification.Public, text: PrimaryClassification[PrimaryClassification.Public]},
            {key: -PrimaryClassification.Sensitive, text: PrimaryClassification[PrimaryClassification.Sensitive]},
            {key: -PrimaryClassification.Confidential, text: PrimaryClassification[PrimaryClassification.Confidential]},
            {key: -PrimaryClassification.Regulated, text: PrimaryClassification[PrimaryClassification.Regulated]},
            {key: -100, text: 'Not Classified'},
          ]
        )
        .sort((a, b) => 
              ((a.primaryClassification || 0) - (b.primaryClassification || 0)) 
          || ((a.secondaryClassificationId || 0) - (b.secondaryClassificationId || 0))),
      documentTypes: (ids4 ?? [])
        .map((id) => {
          const x = data4![id as number]
          const s = { key: x.id, text: x.name }
          return s as any
        })
        .sort((a, b) => a.text < b.text ? -1 : 1),
      infoTypes: (ids3 ?? [])
        .map((id) => {
          const x = data3![id as number]
          const s = { key: `${x.id}-${x.name}`, text: x.name }
          return s as any
        })
        .sort((a, b) => a.text < b.text ? -1 : 1),
      users,
      machines,
      ownerGroups: (ids8 ?? [])
        .map((id) => {
          const c = data8![id as number]
          const s : SimpleOption = c as any
          s.key = c.ownerGroupId!
          s.text = c.name!
          return s as any
        })
        .sort((a, b) => a.text < b.text ? -1 : 1)
    })
  }, [data5, data7, auditUser, accessUser, data6, ids1, ids2, ids4, ids3, ids8, data1, data2, data4, data3, data8])
  return (
    <FileCataAmbientDataContext.Provider value={val}>
      {children}
    </FileCataAmbientDataContext.Provider>
  )
}

export const useFileCataAmbientDataContext = () => useContext(FileCataAmbientDataContext);
